import {useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import {
  BgNavigationTypes,
  ReportActionMenuOptionTypes,
  SourceTypeCode,
  ToastType,
} from '../../../shared/constants';
import ArrActionButton from '../../common/arr-action-button/ArrActionButton';
import {CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import RedistributeDocuments from '../../reports/document-distribution-summary/Redistribute-documents/RedistributeDocuments';
import {
  useCallBulkActionsLikePopup,
  useCallBulkActionsPopup,
  useDoAction,
  useGetBulkAssignDocListId,
} from '../../../hooks/assignments/useAssignments';
import BulkAssignFlyout, {
  BulkAssignContinuePayload,
} from '../bulk-assign-flyout/BulkAssignFlyout';
import ActionLikeDocuments from '../action-like-documents/ActionLikeDocuments';
import {ToastMessageConfigType} from '../../../api/types/ingestionApi.type';
import BulkFolderUnFolder from '../bulk-folder-unfolder/BulkFolderUnFolder';
import BulkTagUntag from '../bulk-tag-untag/BulkTagUntag';
import BulkTaggingWarning from '../bulk-tagging-warning/BulkTaggingWarning';
import {useNavigate} from 'react-router-dom';
import {
  ActionDataType,
  calculateFromPercentage,
  getActionTypeFromMenuItem,
  getCommonExecuteActionPayload,
  getLikeActionMenuDefaultItems,
  getLikeActionType,
  getLikePopupActionType,
  getLikePopUpPayload,
  LikeModalOpenForType,
} from './ActionMenuWrapperHelper';
import {ArrButtonSize} from '../../common/arr-button/ArrButton';
import _ from 'lodash';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../redux/slice/docListSlice';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../redux/slice/docViewSlice';
import {clearDocViewState} from '../../document-viewer/docViewerUtils';
import QuickBatchFlyout from '../quick-batch-flyout/QuickBatchFlyout';
import BulkReleaseFlyout from '../bulk-release-flyout/BulkReleaseFlyout';
import {
  setBulkAssignFlowData,
  setSelectedData,
} from '../../../redux/slice/assignmentSlice';
import {BulkAssignActionOption} from '../bulk-assign-flyout/bulk-assign/BulkAssign';
interface Props {
  actions?: CustomMenuItem[];
  onActionPick?: (
    action: CustomMenuItem,
    callBackForDoc?: (docId: string, source: string) => void
  ) => void;
  isDisabled?: boolean;
  menuClassName?: string;
  dataTestId: string;
  // Use to refresh the report list after action is performed successfully in parent component
  onRefreshReportList?: () => void;
  getRedistributeData?: () => {
    // Pass redistribute details which is rquired for Redistribute action
    redistributeCount: number;
    assignmentId: number;
    currentReviewerId: string;
  };
  /**
   * Set sourceDetails of Report for which action menu is displayed.
   * Detail like report type and its applied filters/ config to get report data.
   */
  sourceDetails?: {[key: string]: any};
  /** Set sourceType Code of Report for which action menu is displayed */
  sourceType: SourceTypeCode;
  /** Set sourceTypeContext Code of Report for which action menu is displayed */
  sourceTypeContext?: string;
  /**
   * Use to get doaction Request payload data as per report type from parent component
   * @param data send the operation type like assign_documents, unassign_documents, sourceDetails and likeActionList
   * @param data.operationType - operation type like assign_documents, unassign_documents
   * @param data.sourceDetails - source details of the report
   * @param data.likeActionList - like action list for the report
   * @returns selectList and destinationList for doAction API request which will added in common request payload.
   */
  getDoActionData?: (data: {
    operationType?: string;
    sourceDetails?: {[key: string]: any};
  }) => {selectedList?: object; destinationList?: object};
  showToastFromParent?: (config: ToastMessageConfigType) => void;
  size?: ArrButtonSize;
  getSourceDetails?: (
    sourceDetails: {[key: string]: any},
    forAPI?: string,
    forAction?: ReportActionMenuOptionTypes | string | null
  ) => {[key: string]: any} | Promise<{[key: string]: any}>;
  subMenuDirection?: 'left' | 'right';
  /** Use additionalParameters for passing extra properties when the payload needs to be modified. */
  additionalParameters?: {[key: string]: any};
  /** This "bulkActionContextTypeCode" props specific to only bulk assign payload.
   * It is different from other action source type based on sightline payload
   */
  bulkActionContextTypeCode?: SourceTypeCode;
  direction?:
    | 'bottom'
    | 'top'
    | 'left'
    | 'right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-left'
    | 'top-right';
  selectedHitsDetails?: {[key: string]: any};
}

const stringPath = 'common.actionMenu.';

const ActionMenuWrapper: React.FC<Props> = ({
  actions = [],
  onActionPick,
  isDisabled,
  menuClassName,
  dataTestId,
  sourceDetails,
  sourceType,
  sourceTypeContext,
  getDoActionData,
  getRedistributeData,
  onRefreshReportList,
  showToastFromParent,
  size = 'medium',
  getSourceDetails,
  subMenuDirection = 'left',
  direction = 'bottom-right',
  additionalParameters = {},
  bulkActionContextTypeCode,
  selectedHitsDetails,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const docListMasterId = useSelector(
    (state: RootState) => state.docView.documentListMasterId
  );
  /** This state is used to hold the selected action from the action menu */
  /** It will be used to show the respective flyout or modal for bulk actions */
  const [selectedAction, setSelectedAction] =
    useState<ReportActionMenuOptionTypes | null>(null);
  /** This state is used to hold the masterDocListID for Bulk Assign / Unassign operation */
  /** It will be used to pass the masterDocListID to doAction API call */
  const [masterDocListID, setMasterDocListID] = useState<number>(0);
  /** This state is used to hold the selected tree ids for Bulk Assign, folder, tag / Unassign doc, folder, tag operation */
  /** It will be used to pass the selected tree ids to doAction API call */
  const [selectedTreeIds, setSelectedTreeIds] = useState<string[]>([]);
  /** This state is used to hold the sub selected operation from action like assign, unassign, folderassign <etc styleName={}></etc> */
  const [bulkSelectedAction, setBulkSelectedAction] = useState<string>('');
  const [isActionLikeModal, setIsActionLikeModal] = useState<boolean | string>(
    false
  );
  const [isBulkWaring, setIsBulkWarning] = useState<boolean>(false);

  /**
   * This state is used to hold the updated action list for like documents.
   * It will be used to pass the updated action list to the ActionLikeDocuments component.
   * Use to pass in doAction API call to get the selected like action list.
   */
  const [updatedLikeActionList, setUpdatedLikeActionList] = useState<any[]>([]);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });
  const [analyticsIndicatorStatus, setAnalyticsIndicatorStatus] =
    useState<number>(0);

  // Ref to store action data for Bulk Assign / Unassign operation and use it in API calls
  // This will be used to get masterDocListID for Bulk Assign action and then used in doAction API call
  const actionDataRef = useRef<ActionDataType | null>(null);

  // To track like open from operation of new tag, folder or assignment creation to assign
  const isLikeOpenWithNewAddon = useRef<boolean>(false);

  // First api call to get masterDocListID when Bulk Assign action is selected
  const {mutate: callBulkActionsPopup, isPending: isPendingBulkActionsPopup} =
    useCallBulkActionsPopup();

  /**
   * API call for submit bulk action config and get masterDocListID for it
   */
  const {
    mutate: getBulkAssignDocListId,
    isPending: isPendingBulkAssignDocListId,
  } = useGetBulkAssignDocListId();

  /** API call to likeActionPopup after  getBulkAssignDocListId called with new masterDocListD */
  const {
    mutate: callBulkActionsLikePopup,
    isPending: isLoadingBulkActionsLikePopup,
  } = useCallBulkActionsLikePopup();

  /**
   * API call to submit the bulk action data and completed assignment
   */
  const {mutate: doAction, isPending: isDoActionLoading} = useDoAction();

  /** Check if any of the API calls are pending to show loading spinner */
  const isLoading = useMemo(() => {
    return (
      isDoActionLoading ||
      isPendingBulkActionsPopup ||
      isPendingBulkAssignDocListId ||
      isLoadingBulkActionsLikePopup
    );
  }, [
    isDoActionLoading,
    isPendingBulkActionsPopup,
    isPendingBulkAssignDocListId,
    isLoadingBulkActionsLikePopup,
  ]);

  useEffect(() => {
    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      const {navigationDestinationType: navType, notificationId} =
        lastEvent?.payload?.payload || {};

      if (
        notificationId === Number(docListMasterId) &&
        navType === SourceTypeCode.DOCVIEW
      ) {
        if (selectedAction === ReportActionMenuOptionTypes.ViewInDocView) {
          navigate('/DocumentViewer/DocView');
        } else if (
          selectedAction === ReportActionMenuOptionTypes.ViewInDocViewNewTab
        ) {
          const newWindow = window.open('about:blank', '_blank');
          if (newWindow) {
            newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
          }
        }
      }

      if (
        notificationId === Number(docListMasterId) &&
        navType === SourceTypeCode.DOCLIST
      ) {
        if (selectedAction === 'ViewInDocList') {
          const backPath = `${window.location.pathname}${window.location.search}`;
          dispatch(
            setDocListBackNavigation({
              label: t('docExplorer.header.title'),
              path: backPath,
            })
          );
          navigate('/Document/DocList');
        }
      }
    }
  }, [selectedAction, lastEvent, docListMasterId]);

  /** Prepare the payload for executing bulk action for doAction API call */
  const getExecuteBulkActionPayload = (
    actionType: string,
    masterDocId?: number
  ): object => {
    const selectedTreeIdsToUse =
      actionDataRef.current?.selectedSecurityGroupIds ?? selectedTreeIds;
    const updateSelectedAction =
      actionDataRef.current?.selectedBulkReleaseAction ?? bulkSelectedAction;
    const payload = getCommonExecuteActionPayload(
      actionType,
      masterDocId ?? masterDocListID,
      sourceDetails,
      actionType == 'ASSIGN'
        ? (bulkActionContextTypeCode ?? sourceType)
        : sourceType, // Special case for assign flow in doclist page
      actionDataRef.current,
      selectedTreeIdsToUse,
      updateSelectedAction,
      updatedLikeActionList.length > 0
        ? updatedLikeActionList
        : getLikeActionMenuDefaultItems(t),
      selectedHitsDetails
    );

    // Get data from parent component if provided using getDoActionData so it will be
    // used in doAction API call as per report type
    if (getDoActionData) {
      const {selectedList, destinationList} = getDoActionData({
        operationType: bulkSelectedAction,
        sourceDetails: sourceDetails,
      });

      payload.selectedList = selectedList;
      payload.destinationList = {
        ...payload.destinationList,
        ...destinationList,
      };
    }

    if (updateSelectedAction === 'bulkUnrelease') {
      delete payload.IsAdd;
      if (payload.destinationList) {
        payload.destinationList.IsAdd = false;
        delete payload.destinationList.IncludeEmailThreads;
        delete payload.destinationList.IncludeFamilyMemeber;
        delete payload.destinationList.IncludeNearDuplicates;
        delete payload.destinationList.MasterDocListID;
      }
    }
    return payload;
  };

  const executeDoAction = (actionType: string, masterId?: number) => {
    doAction(getExecuteBulkActionPayload(actionType, masterId), {
      onSuccess: (response) => {
        let msg = response.message ?? t(stringPath + 'doActionSuccess');
        if (selectedAction?.toLowerCase() === 'quickbatch')
          msg = t('docExplorer.quickBatchSuccessMessage');

        if (actionType === 'ASSIGN' && actionDataRef.current?.isNewAssignment) {
          //Update the state of bulkAssignFlowData and selectedData before navigating to new assignment page.
          dispatch(
            setBulkAssignFlowData({
              assignmentId: actionDataRef.current.assignmentId!,
              bulkActionId: response.bulkActionId,
              masterDocListId: masterDocListID,
              parentGroupId: Number(selectedTreeIds[0]),
            })
          );

          dispatch(
            setSelectedData({
              assignmentId: actionDataRef.current.assignmentId!,
              mode: 'create',
              type: 'assignment',
              assignmentGroupId: Number(selectedTreeIds[0]),
            })
          );

          // navigate to new assignment route
          navigate(
            `/Assignment/Create?AssignmentId=${actionDataRef.current.assignmentId}`
          );
        } else {
          setToastMsgConfig({
            show: true,
            title: t('common.success'),
            type: ToastType.Success,
            message: msg,
          });
        }
        setSelectedAction(null);
        setIsActionLikeModal(false);
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          title: t('common.error'),
          type: ToastType.Error,
          message:
            error?.response?.data?.title ?? t(stringPath + 'doActionError'),
        });
      },
    });
  };

  const executeLikePopupCall = (
    actionType: string,
    masterDocId: number,
    likeModalOpenFor: LikeModalOpenForType,
    aditionalData: {
      docAssign?: number;
      newFolderId?: number; // recevied only new folder is created and used in bulkFolder action
      newTagId?: number; // recevied only new tag is created and used in bulkTag action
    }
  ) => {
    const updatedSourceDetails =
      getSourceDetails?.(sourceDetails ?? {}, 'getLikePopup', selectedAction) ??
      _.cloneDeep(sourceDetails);
    const selectedTreeIdsToUse =
      actionDataRef.current?.selectedSecurityGroupIds ?? selectedTreeIds;

    const payload = getLikePopUpPayload(
      actionType,
      masterDocId,
      sourceType,
      updatedSourceDetails,
      aditionalData,
      actionDataRef.current,
      selectedTreeIdsToUse,
      selectedHitsDetails
    );

    callBulkActionsLikePopup(payload, {
      onSuccess: (response) => {
        setAnalyticsIndicatorStatus(response.analyticsIndicatorStatus);
        setIsActionLikeModal(likeModalOpenFor);
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          title: t('common.error'),
          message:
            error?.response?.data?.title ?? t(stringPath + 'bulkTagError'),
        });
      },
    });
  };

  const handleActionLike = () => {
    const actionType = getLikeActionType(bulkSelectedAction);
    if (actionType === 'TAG') {
      setIsActionLikeModal(false);
      setIsBulkWarning(true);
    } else {
      const acType =
        actionType === 'ASSIGNMENT' || actionType === 'QUICKASSIGNMENT';
      executeDoAction(acType ? 'ASSIGN' : actionType);
    }
  };

  const handleBulkFolderContinue = async (
    action: string,
    docCount?: number,
    newFolderId?: number
  ) => {
    isLikeOpenWithNewAddon.current = _.isNil(newFolderId) ? false : true;
    setBulkSelectedAction(action);

    if (action === 'folderDocs') {
      actionDataRef.current = {
        isAdd: true,
        sampleMethod: 'STANDARD',
        numberToAssign: (docCount ?? 0).toString(),
      };
      if (newFolderId) {
        setSelectedTreeIds([newFolderId.toString()]);
      }
      // If action is folderDocs, then we need to execute like popup call
      executeLikePopupCall('FOLDER', masterDocListID, 'bulkFolder', {
        docAssign: docCount ?? 0,
        newFolderId: newFolderId,
      });
    } else if (action === 'unfolderDocs') {
      // If action is unfolderDocs, then we need to execute doAction API call
      actionDataRef.current = {
        isAdd: false,
        sampleMethod: 'STANDARD',
        numberToAssign: (docCount ?? 0).toString(),
      };
      executeDoAction('FOLDER');
    }
  };

  const handleBulkTagContinue = (
    action: string,
    docCount?: number,
    newTagId?: number
  ) => {
    setBulkSelectedAction(action);
    isLikeOpenWithNewAddon.current = _.isNil(newTagId) ? false : true;
    if (action === 'tagDocs') {
      actionDataRef.current = {
        isAdd: true,
        sampleMethod: 'STANDARD',
        numberToAssign: (docCount ?? 0).toString(),
      };
      if (newTagId) {
        setSelectedTreeIds([newTagId.toString()]);
      }
      // If action is tagDocs, then we need to execute like popup call
      executeLikePopupCall('TAG', masterDocListID, 'bulkTag', {
        docAssign: docCount ?? 0,
        newTagId: newTagId,
      });
    } else if (action === 'untagDocs') {
      // If action is untagDocs, then we need to execute doAction API call
      actionDataRef.current = {
        isAdd: false,
        sampleMethod: 'STANDARD',
        numberToAssign: (docCount ?? 0).toString(),
      };
      // Show Bulk Warning popup for untag action
      setIsBulkWarning(true);
    }
  };

  const handleBulkAssignContinue = (payload: BulkAssignContinuePayload) => {
    const {
      action,
      isNewAssignment,
      docCount,
      samplingMethod,
      persistContent,
      count,
      percent,
    } = payload;
    setBulkSelectedAction(action);

    isLikeOpenWithNewAddon.current = isNewAssignment;
    const isAdd = action !== 'unassign_documents';
    const isNew = isAdd ? isNewAssignment : false;
    const docListPayload: {[key: string]: any} = {
      ActionType: 'ASSIGNMENT',
      ElementID: 'divBulkAction',
      BulkActionContextTypeCode: bulkActionContextTypeCode ?? sourceType,
      DocumentCount: '0',
      ContextContainer: getSourceDetails
        ? getSourceDetails(
            sourceDetails ?? {},
            'getBulkDocListId',
            selectedAction
          )
        : sourceDetails,
      UniqueDocumentCount: '0',
      DestinationContextTypeCode: 'BULKASSIGN',
      SampleMethod: 'STANDARD',
      NumberToAssign: (docCount ?? 0).toString(),
      IsPersistSearchHits: false,
      SourceContextTypeCode: 'ASSIGNDOCNOC',
      BulkActionTreeIDs: selectedTreeIds,
      IsAdd: isAdd,
      IsNewAssignment: isNew,
      SourceContextID: masterDocListID.toString(),
      BulkActionTypeCode: 'ASSIGN',
    };
    let docAssignCount = -1;
    if (action === 'assign_documents') {
      docListPayload.IsPersistSearchHits = persistContent ?? false;
      switch (samplingMethod) {
        case 'count':
          docListPayload.SampleMethod = 'STANDARD';
          docListPayload.NumberToAssign = count?.toString();
          docAssignCount = count ?? 0;
          break;
        case 'percent':
          docListPayload.SampleMethod = 'PERCENTAGE';
          docListPayload.NumberToAssign = percent?.toString();
          docAssignCount = calculateFromPercentage(docCount ?? 0, percent ?? 0);
          break;
        case 'parent_level_docs_only':
          docListPayload.NumberToAssign = '-1';
          docListPayload.SampleMethod = 'PARENTLEVEL';
          break;
        case 'inclusive_email':
          docListPayload.SampleMethod = 'INCLUSIVEEMAIL';
          docListPayload.NumberToAssign = '-1';
          break;
        default:
          docListPayload.SampleMethod = 'STANDARD';
          break;
      }
    }

    actionDataRef.current = {
      isAdd: isAdd,
      isNewAssignment: isNewAssignment,
      isNew: isNew,
      persistContent: persistContent ?? false,
      sampleMethod: docListPayload.SampleMethod,
      numberToAssign:
        samplingMethod === 'percent'
          ? docAssignCount
          : docListPayload.NumberToAssign,
      docCount: docCount ?? 0,
    };

    getBulkAssignDocListId(docListPayload, {
      onSuccess: (response) => {
        actionDataRef.current = {
          ...actionDataRef.current,
          assignmentId: response.assignmentId,
        };
        setMasterDocListID(response.masterDocListID);
        if (action === 'assign_documents') {
          executeLikePopupCall(
            'ASSIGN',
            response.masterDocListID,
            'bulkAssign',
            {
              docAssign: docAssignCount,
            }
          );
        } else {
          executeDoAction('ASSIGN', response.masterDocListID);
        }
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          title: t('common.error'),
          message:
            error?.response?.data?.title ?? t(stringPath + 'bulkTagError'),
        });
      },
    });
  };

  const handleQuickBatchContinue = (
    assignmentId?: number,
    docCount?: number,
    action?: string
  ) => {
    if (action) {
      setBulkSelectedAction(action);
      setSelectedAction(ReportActionMenuOptionTypes.QuickBatch);
    }

    isLikeOpenWithNewAddon.current = false;
    actionDataRef.current = {
      isAdd: true,
      sampleMethod: 'STANDARD',
      numberToAssign: (docCount ?? 0).toString(),
    };
    const createBulkTreeIds = assignmentId ?? 0;
    const quickBatchDocIdPayload: {[key: string]: any} = {
      ActionType: 'QUICKASSIGNMENT',
      ElementID: 'divBulkAction',
      BulkActionContextTypeCode: bulkActionContextTypeCode ?? sourceType,
      DocumentCount: '0',
      ContextContainer: getSourceDetails
        ? getSourceDetails(
            sourceDetails ?? {},
            'getBulkDocListId',
            selectedAction
          )
        : sourceDetails,
      UniqueDocumentCount: 0,
      DestinationContextTypeCode: 'BULKASSIGN',
      SampleMethod: 'STANDARD',
      SourceContextTypeCode: 'ASSIGNDOCNOC',
      BulkActionTreeIDs: [createBulkTreeIds],
      IsAdd: true,
      IsNewAssignment: false,
      IsPersistSearchHits: true,
      IsQuick: true,
      BulkActionTypeCode: 'ASSIGN',
      SourceContextID: masterDocListID.toString(),
      NumberToAssign: (docCount ?? 0).toString(),
    };
    getBulkAssignDocListId(quickBatchDocIdPayload, {
      onSuccess: (response) => {
        setMasterDocListID(response.masterDocListID);
        actionDataRef.current = {
          ...actionDataRef.current,
          assignmentId: assignmentId ?? 0,
          selectedAction:
            selectedAction ?? ReportActionMenuOptionTypes.QuickBatch,
        };
        executeLikePopupCall(
          'QUICKASSIGNMENT',
          response.masterDocListID,
          'quickBatch',
          {
            docAssign: docCount ?? 0,
          }
        );
      },
    });
  };

  const handleBulkRelease = (selectedIds: string[]) => {
    setBulkSelectedAction('bulkRelease');
    setSelectedTreeIds(selectedIds ?? []);
    actionDataRef.current = {
      isAdd: true,
      sampleMethod: 'STANDARD',
      selectedSecurityGroupIds: selectedIds,
    };
    executeLikePopupCall('RELEASE', masterDocListID, 'bulkRelease', {});
  };

  const handleBulkUnrelease = (selectedIds: string[]) => {
    setBulkSelectedAction('bulkUnrelease');
    setSelectedTreeIds(selectedIds ?? []);
    actionDataRef.current = {
      isAdd: false,
      sampleMethod: 'STANDARD',
      selectedSecurityGroupIds: selectedIds,
      selectedBulkReleaseAction: 'bulkUnrelease',
    };
    executeDoAction('RELEASE');
    setSelectedAction(null);
  };

  const onActionSelect = (action: CustomMenuItem) => {
    setUpdatedLikeActionList([]);
    if (!action) return;
    const actionId = action.id as ReportActionMenuOptionTypes;

    switch (actionId) {
      case ReportActionMenuOptionTypes.BulkTag:
      case ReportActionMenuOptionTypes.BulkFolder:
      case ReportActionMenuOptionTypes.BulkAssign:
      case ReportActionMenuOptionTypes.QuickBatch:
      case ReportActionMenuOptionTypes.BulkRelease:
      case ReportActionMenuOptionTypes.Unassign: {
        const updatedSourceDetails: {[key: string]: any} = {
          ...(sourceDetails ?? {}),
        };
        if (actionId === ReportActionMenuOptionTypes.Unassign) {
          updatedSourceDetails.SubActionType = 'IsUnAssigned';
        }

        // Get sourceDetails - wait for API call to complete before calling popup
        const sourceDetailsResult =
          getSourceDetails?.(
            updatedSourceDetails,
            'getBulkActionPopup',
            actionId
          ) ?? updatedSourceDetails;
        const sourceDetailsPromise =
          sourceDetailsResult instanceof Promise
            ? sourceDetailsResult
            : Promise.resolve(sourceDetailsResult || {});

        // Wait for communication-data API call to complete, then call popup
        sourceDetailsPromise.then((resolvedSourceDetails) => {
          let commonPayload = {
            sourceType:
              actionId.toLowerCase() === 'quickbatch' &&
              sourceType.toLowerCase() === 'doclist'
                ? 'DOCUMENT'
                : sourceType,
            actionType: getActionTypeFromMenuItem(actionId),
            elementID: 'divBulkAction',
            selectedHitsDetails: selectedHitsDetails ?? null,
            sourceDetails: JSON.stringify(resolvedSourceDetails ?? {}),
            sourceTypeContext: sourceTypeContext,
            documentCount: '',
          };
          if (additionalParameters) {
            commonPayload = {...commonPayload, ...additionalParameters};
          }
          callBulkActionsPopup(commonPayload, {
            onSuccess: (response) => {
              setMasterDocListID(response.masterDocListID);
              setSelectedAction(actionId);
            },
            onError: (error: any) => {
              setToastMsgConfig({
                show: true,
                type: ToastType.Error,
                title: t('common.error'),
                message:
                  error?.response?.data?.title ??
                  t(stringPath + 'bulkTagError'),
              });
            },
          });
        });
        break;
      }
      case ReportActionMenuOptionTypes.Redistribute:
        setSelectedAction(actionId);
        break;
      case ReportActionMenuOptionTypes.ViewInDocList:
      case ReportActionMenuOptionTypes.ViewInDocViewNewTab:
      case ReportActionMenuOptionTypes.ViewInDocView: {
        setSelectedAction(actionId);
        onActionPick &&
          onActionPick(action, (docId: string, source: string) => {
            // Callback function to get docId from parent component to open document view flow
            clearDocViewState();
            dispatch(storeDocumentListMasterId(docId));
            dispatch(storeSourceTypeCode(source));

            if (actionId === ReportActionMenuOptionTypes.ViewInDocList) {
              dispatch(setDocListId(Number(docId)));
            }
          });
        break;
      }
      default: {
        onActionPick && onActionPick(action);
        break;
      }
    }
  };

  const closeParentActionFlyoutForCancel = () => {
    if (isLikeOpenWithNewAddon.current) {
      if (sourceType != 'TALLY') {
        setSelectedAction(null);
      }
    }
  };

  const renderRedistrubuteDrawer = () => {
    if (
      selectedAction === ReportActionMenuOptionTypes.Redistribute &&
      getRedistributeData
    ) {
      const props = getRedistributeData!();
      return (
        <RedistributeDocuments
          showDrawer
          onClose={() => setSelectedAction(null)}
          onSuccess={(message) => {
            if (message && showToastFromParent)
              showToastFromParent({
                type: ToastType.Success,
                message: message,
              });
            setSelectedAction(null);
            // Call parent component function to refresh the report list after successful redistribute action
            onRefreshReportList && onRefreshReportList();
          }}
          sourceType={sourceType}
          {...props}
        />
      );
    }
    return null;
  };

  return (
    <>
      <ArrActionButton
        menuItems={actions}
        size={size}
        isBtnDisabled={isDisabled}
        menuClassName={menuClassName}
        autoFocusMenu
        disableAutoFocusItem={false}
        direction={direction}
        subMenuDirection={subMenuDirection}
        dataTestId={dataTestId ?? 'report-action-menu'}
        onActionPick={onActionSelect}
      />
      {(selectedAction === ReportActionMenuOptionTypes.BulkAssign ||
        selectedAction === ReportActionMenuOptionTypes.Unassign) && (
        <BulkAssignFlyout
          isOpen
          onClose={() => setSelectedAction(null)}
          masterDocListId={masterDocListID}
          onTreeSelect={(ids) => setSelectedTreeIds(ids ?? [])}
          onContinue={handleBulkAssignContinue}
          initialSelectedAction={
            selectedAction === ReportActionMenuOptionTypes.Unassign
              ? BulkAssignActionOption.UnassignDoc
              : BulkAssignActionOption.AssignDoc
          }
        />
      )}
      {selectedAction === ReportActionMenuOptionTypes.BulkFolder && (
        <BulkFolderUnFolder
          data-testid="bulk-folder-unfolder"
          isOpen
          setOpenActionMenu={(value) => {
            if (!value) setSelectedAction(null);
          }}
          masterDocListId={masterDocListID}
          setSelectedNewExistingFolder={(event) =>
            setSelectedTreeIds((event?.ids ?? []) as string[])
          }
          handleBulkFolderContinue={handleBulkFolderContinue}
        />
      )}
      {selectedAction === ReportActionMenuOptionTypes.BulkTag && (
        <BulkTagUntag
          isOpen
          setOpenActionMenu={(value) => {
            if (!value) setSelectedAction(null);
          }}
          masterDocListId={masterDocListID}
          setSelectedNewExistingTag={(event) =>
            setSelectedTreeIds((event?.ids ?? []) as string[])
          }
          handleBulkTagContinue={handleBulkTagContinue}
        />
      )}
      {selectedAction === ReportActionMenuOptionTypes.QuickBatch && (
        <QuickBatchFlyout
          isOpen
          onClose={() => setSelectedAction(null)}
          onContinue={handleQuickBatchContinue}
          masterDocListId={masterDocListID}
        />
      )}
      {selectedAction === ReportActionMenuOptionTypes.BulkRelease && (
        <BulkReleaseFlyout
          isOpen
          onClose={() => setSelectedAction(null)}
          onRelease={handleBulkRelease}
          onUnrelease={handleBulkUnrelease}
        />
      )}
      {isActionLikeModal !== false && (
        <ActionLikeDocuments
          dataTestId="action-like"
          showModal={!!isActionLikeModal}
          onClose={() => {
            setIsActionLikeModal(false);
            closeParentActionFlyoutForCancel();
          }}
          actionList={getLikeActionMenuDefaultItems(t)}
          onCancel={() => {
            setIsActionLikeModal(false);
            closeParentActionFlyoutForCancel();
          }}
          continueButtonLabel={t(`${stringPath}finalize.${isActionLikeModal}`)}
          onContinue={handleActionLike}
          onActionChange={(_action, _status, updatedActions) =>
            setUpdatedLikeActionList(updatedActions)
          }
          masterDocListId={masterDocListID}
          actionType={getLikePopupActionType(isActionLikeModal as string)}
          analyticsIndicatorStatus={analyticsIndicatorStatus}
        />
      )}
      {isBulkWaring && (
        <BulkTaggingWarning
          showModal
          onClose={() => {
            setIsBulkWarning(false);
            closeParentActionFlyoutForCancel();
          }}
          sourceType={sourceType}
          masterDocListId={masterDocListID}
          selectedNewExistingTag={selectedTreeIds}
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setIsBulkWarning(false);
                closeParentActionFlyoutForCancel();
              },
              variant: 'secondary',
              dataTestId: 'bulk-tag-cancel',
            },
            {
              id: '2',
              label: t('common.ok'),
              onClick: () => {
                setIsBulkWarning(false);
                executeDoAction('TAG');
              },
              variant: 'primary',
              dataTestId: 'bulk-tag-ok',
            },
          ]}
        />
      )}
      {renderRedistrubuteDrawer()}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
      {isLoading && <ArrLoadingSpinner isOpen overlay />}
    </>
  );
};

export default ActionMenuWrapper;
